import { Component, DestroyRef, inject, input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { ConsumerJsonModel } from '../../consumer/consumer.json.model';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { ConsumerData, MetadataFieldDataTypes } from '../../core/types';
import { MatInput } from '@angular/material/input';
import { JsonPipe } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FunctionHelpService } from '../../functionalHelp/services/functionHelp.service';
import { MatButtonModule } from '@angular/material/button';
import { FunctionalHelpJsonModel } from '../../functionalHelp/functionalHelp.json.model';
import { FunctionalHelpDialogComponent } from '../../core/components/functional-help-dialog/functional-help-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { GlobalMetadataJsonModel } from '../../global-metadata/global-metadata.json.model';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

// Definieren des Typs für die "functionalHelp"-Unterformulargruppe
interface FunctionalHelpGroup {
  id: string | null
  showHelpOnStart: boolean | null
}

interface FunctionalHelpData extends Record<string, unknown> {
  functionalHelp?: FunctionalHelpGroup
}

export enum LearnItemMetaDataComponentMode {
  GLOBAL = 0,
  LEARN_ITEM = 1,
}

@Component({
  selector: 'pixl-learnitem-metadata',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    JsonPipe,
    MatExpansionModule,
    MatSlideToggle,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule,
    MatIconModule,
    TranslocoModule
  ],
  templateUrl: './learnitem-metadata.component.html',
  styleUrl: './learnitem-metadata.component.scss',
})
export class LearnitemMetadataComponent implements OnChanges {
  // @Input()-Deklarationen durch Signal-basierte Inputs ersetzen
  private translocoService = inject(TranslocoService);

  public mode = input(LearnItemMetaDataComponentMode.GLOBAL);
  public formGroup = input.required<FormGroup>();
  public learnObjectType = input<string | undefined>();
  public consumer = input<ConsumerJsonModel | undefined>();
  public learnItem = input<{ consumerData?: ConsumerData } | undefined>();
  public customMetadata = input<GlobalMetadataJsonModel[] | undefined>();

  private functionHelpService = inject(FunctionHelpService);
  private formBuilder = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  public approval = input<boolean>(false);
  public metaData?: GlobalMetadataJsonModel[];
  public dataTypes = MetadataFieldDataTypes;

  public functionalHelpName = signal('');
  public showFunctionalHelpSignal = signal(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    if ((this.learnObjectType() || this.customMetadata()) && this.formGroup() && this.consumer() && this.learnItem()) {
      if (this.customMetadata()) {
        this.metaData = this.customMetadata();
      }
      else if (this.mode() === LearnItemMetaDataComponentMode.GLOBAL) {
        this.metaData = this.consumer()!.consumerMetadata;
      }
      else if (this.mode() === LearnItemMetaDataComponentMode.LEARN_ITEM) {
        this.metaData = this.consumer()!.learnObjectTypeDescriptions.find((v) => {
          return v.label === this.learnObjectType();
        })?.metadata;
      }
      this.initForm();
    }
  }

  initForm() {
    const consumerData: FunctionalHelpData = this.learnItem()?.consumerData || {};

    let consumerDataGroup = this.formGroup().get('consumerData') as FormGroup;
    if (!consumerDataGroup) {
      consumerDataGroup = this.formBuilder.group({});
      this.formGroup().addControl('consumerData', consumerDataGroup);
    }

    const hasFunctionalHelp = this.metaData?.some((m) =>
      m.fieldConf?.some((f) => f.datatype === 'FunctionalHelp')
    );

    if (hasFunctionalHelp) {
      if (!consumerDataGroup.contains('functionalHelp')) {
        const functionalHelpGroup = this.formBuilder.group<FunctionalHelpGroup>({
          id: consumerData?.functionalHelp?.id || null,
          showHelpOnStart: consumerData?.functionalHelp?.showHelpOnStart ?? false,
        });
        consumerDataGroup.addControl('functionalHelp', functionalHelpGroup);

        if (consumerData.functionalHelp && consumerData.functionalHelp.id) {
          this.functionHelpService
            .getOne(consumerData.functionalHelp.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((answer) => {
              if (answer && answer.data) {
                this.functionalHelpName.set(this.translocoService.translate(answer.data.name));
                functionalHelpGroup.patchValue({
                  id: answer.data.id,
                  showHelpOnStart: answer.data.showHelpOnStart ?? null,
                });
                this.showFunctionalHelpSignal.set(true);
              }
            });
        }
      }
    }
    else if (consumerDataGroup.contains('functionalHelp')) {
      consumerDataGroup.removeControl('functionalHelp');
      this.showFunctionalHelpSignal.set(false);
      this.functionalHelpName.set('');
    }

    this.metaData?.forEach((m) => {
      m.fieldConf.forEach((f) => {
        if (f.datatype === 'FunctionalHelp') {
          return;
        }
        if (consumerDataGroup.contains(f.name)) {
          return;
        }
        const value = this.learnItem()?.consumerData?.[f.name] ?? null;
        const validatorOpts = f.mandatory && !this.approval() ? [Validators.required] : [];
        consumerDataGroup.addControl(
          f.name,
          this.formBuilder.control({ value: value, disabled: this.approval() }, validatorOpts)
        );
      });
    });

    this.formGroup().valueChanges.subscribe((values) => {
      if (this.learnItem() && this.learnItem()!.consumerData !== undefined) {
        this.learnItem()!.consumerData = {
          ...this.learnItem()!.consumerData,
          ...this.filterValidValues(values.consumerData),
        };
      }
    });
  }

  private filterValidValues(values: ConsumerData): ConsumerData {
    return Object.keys(values).reduce((acc, key) => {
      if (values[key] !== null && values[key] !== undefined) {
        (acc as ConsumerData)[key] = values[key];
      }
      return acc;
    }, {});
  }

  public checkUnsignedIntValue(ev: Event): void {
    const val = (ev.target as HTMLInputElement).value;
    if (val) {
      if (parseInt(val, 10) < 0) {
        (ev.target as HTMLInputElement).value = '0';
      }
      if (val.length > 1 && parseInt(val[0], 10) === 0) {
        (ev.target as HTMLInputElement).value = val.substring(1, val.length);
      }
    }
  }

  public showFunctionalHelp() {
    const functionalHelpJsonModel = new FunctionalHelpJsonModel({
      id: '',
      name: '',
    });

    const dialogRef = this.dialog.open(FunctionalHelpDialogComponent, {
      minWidth: '1000px',
      height: '70vh',
      data: functionalHelpJsonModel,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dialogData: FunctionalHelpJsonModel) => {
        if (dialogData) {
          const functionalHelpGroup = this.formGroup().get('consumerData.functionalHelp') as FormGroup;
          if (functionalHelpGroup) {
            functionalHelpGroup.patchValue({
              id: dialogData.id,
            });
            this.functionalHelpName.set(this.translocoService.translate(dialogData.name));
          }
        }
      });
  }
}
